import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { MessageSquare, RefreshCw } from 'lucide-react';

/**
 * Review Pipeline log for one patient — shown inside PatientProfileModal.
 *
 * Statuses mean:
 *   feedback_asked      → WhatsApp feedback-ask sent (3 URL buttons), awaiting click
 *   task_sent           → Rated 5/4; reward-task template sent, awaiting screenshot upload
 *   screenshot_received → Screenshot uploaded via link; pending reviewer decision
 *   approved            → Reviewer approved — coupon issued
 *   rejected            → Reviewer rejected — task still counts as complete
 *   rated_no_task       → Rated 5/4 but the cascade is exhausted
 *   low_feedback_closed → Rated 3 or less; apology sent + admin alerted
 *   asked / verified / declined → legacy WA-callback flow statuses
 */

const statusPill = {
  feedback_asked: { label: 'Feedback asked', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  task_sent: { label: 'Reward task sent', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  screenshot_received: { label: 'Screenshot received', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  approved: { label: 'Approved · Coupon issued', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-200' },
  rated_no_task: { label: 'Rated · no task left', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  low_feedback_closed: { label: 'Low rating (closed)', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  asked: { label: 'Review asked (legacy)', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  verified: { label: 'Coupon issued (legacy)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  declined: { label: 'Declined (legacy)', cls: 'bg-red-50 text-red-700 border-red-100' },
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) {
    return iso;
  }
};

const scoreLabel = (s) => {
  const n = Number(s || 0);
  if (n === 5) return '5';
  if (n > 0) return '4 or less';
  return null;
};

const PatientReviewPipelineSection = ({ patientId }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const res = await api.get(`/patients/${patientId}/review-tasks`);
      setRows(res.data?.rows || []);
    } catch (_e) {
      toast.error('Failed to load review pipeline');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const verifiedRow = rows.find(r => r.status === 'approved' || r.status === 'verified');

  return (
    <div data-testid="patient-review-pipeline-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-indigo-600" />
          Review Pipeline
          <Badge variant="outline" className="text-xs">{rows.length}</Badge>
          {verifiedRow && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
              Completed
            </Badge>
          )}
        </h3>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
          data-testid="patient-review-pipeline-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 py-3">Loading pipeline…</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4 text-center border border-dashed border-gray-200">
          No review tasks yet. A feedback ask fires on every eligible check-out (Ameesha instantly, others T+4 days).
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
          {rows.map((r) => {
            const pill = statusPill[r.status] || { label: r.status || '—', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
            const score = scoreLabel(r.feedback_score);
            return (
              <div
                key={r.id}
                className="p-3 hover:bg-gray-50"
                data-testid={`review-task-${r.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-xs font-semibold ${pill.cls}`}>{pill.label}</Badge>
                  <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                    {r.profile || 'eden'}
                  </span>
                  {score !== null && (
                    <Badge variant="outline" className="text-xs">Score {score}</Badge>
                  )}
                  {r.reward_task_name && (
                    <Badge variant="outline" className="text-xs">{r.reward_task_name}</Badge>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">{fmtWhen(r.feedback_asked_at)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1.5">
                  {r.source_service_name && <span>{r.source_service_name}</span>}
                  {r.source_appointment_ref && <span> · {r.source_appointment_ref}</span>}
                </div>
                {(r.screenshot_received_at || r.verified_at || r.approved_at || r.rejected_at) && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.screenshot_received_at && <span>Screenshot {fmtWhen(r.screenshot_received_at)} </span>}
                    {(r.approved_at || r.verified_at) && (
                      <span>· Approved {fmtWhen(r.approved_at || r.verified_at)}{r.coupon_amount ? ` · ₹${r.coupon_amount} coupon` : ''}</span>
                    )}
                    {r.rejected_at && <span>· Rejected {fmtWhen(r.rejected_at)}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PatientReviewPipelineSection;
